import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import DownloadIcon from '@mui/icons-material/Download';
import { getOperatorsReport } from '../api';

interface OperatorReport {
  operatorName: string;
  month: string;
  hoursOnLine: number;
  callsCount: number;
  leadsCount: number;
  avgCallDuration: number;
  avgQualityScore: number;
  hourlyRate: number;
  baseSalary: number;
  perLeadBonus: number;
  volumeBonus: number;
  totalSalary: number;
  workDays: number;
  avgHoursPerDay: number;
}

interface OperatorReportSummary {
  month: string;
  totalOperators: number;
  totalHours: number;
  totalCalls: number;
  totalLeads: number;
  totalSalary: number;
  operators: OperatorReport[];
}

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 1,
});

export const OperatorReportPage: React.FC = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState<OperatorReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOperatorsReport(month);
      setReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const exportToCSV = () => {
    if (!report) return;

    const headers = [
      'Оператор',
      'Дни',
      'Часы',
      'Ср. ч/день',
      'Звонки',
      'Лиды',
      'Качество',
      'Ставка/час',
      'Базовая ЗП',
      'Бонус за лиды',
      'Бонус за объём',
      'Итого ЗП',
    ];

    const rows = report.operators.map((op) => [
      op.operatorName,
      op.workDays,
      op.hoursOnLine.toFixed(1),
      op.avgHoursPerDay.toFixed(1),
      op.callsCount,
      op.leadsCount,
      op.avgQualityScore.toFixed(1),
      op.hourlyRate,
      op.baseSalary.toFixed(0),
      op.perLeadBonus.toFixed(0),
      op.volumeBonus.toFixed(0),
      op.totalSalary.toFixed(0),
    ]);

    // Add totals row
    rows.push([
      'ИТОГО',
      '',
      report.totalHours.toFixed(1),
      '',
      report.totalCalls.toString(),
      report.totalLeads.toString(),
      '',
      '',
      '',
      '',
      '',
      report.totalSalary.toFixed(0),
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.join(';')),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `operators-report-${month}.csv`;
    link.click();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Отчёт по операторам</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Период"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={!report || report.operators.length === 0}
          >
            Экспорт CSV
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Операторов</Typography>
                  <Typography variant="h5">{report.totalOperators}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Всего часов</Typography>
                  <Typography variant="h5">{numberFormatter.format(report.totalHours)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Всего звонков</Typography>
                  <Typography variant="h5">{numberFormatter.format(report.totalCalls)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Всего лидов</Typography>
                  <Typography variant="h5">{numberFormatter.format(report.totalLeads)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Общая ЗП</Typography>
                  <Typography variant="h5">{currencyFormatter.format(report.totalSalary)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Operators Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Оператор</TableCell>
                  <TableCell align="right">Дни</TableCell>
                  <TableCell align="right">Часы</TableCell>
                  <TableCell align="right">Ср. ч/день</TableCell>
                  <TableCell align="right">Звонки</TableCell>
                  <TableCell align="right">Лиды</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Средняя оценка качества">
                      <span>Качество</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Ставка/час</TableCell>
                  <TableCell align="right">Базовая ЗП</TableCell>
                  <TableCell align="right">Бонус лиды</TableCell>
                  <TableCell align="right">Бонус объём</TableCell>
                  <TableCell align="right">Итого ЗП</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.operators.map((op) => (
                  <TableRow key={op.operatorName} hover>
                    <TableCell>{op.operatorName}</TableCell>
                    <TableCell align="right">{op.workDays}</TableCell>
                    <TableCell align="right">{numberFormatter.format(op.hoursOnLine)}</TableCell>
                    <TableCell align="right">{numberFormatter.format(op.avgHoursPerDay)}</TableCell>
                    <TableCell align="right">{numberFormatter.format(op.callsCount)}</TableCell>
                    <TableCell align="right">{op.leadsCount}</TableCell>
                    <TableCell align="right">
                      {op.avgQualityScore > 0 ? numberFormatter.format(op.avgQualityScore) : '—'}
                    </TableCell>
                    <TableCell align="right">{currencyFormatter.format(op.hourlyRate)}</TableCell>
                    <TableCell align="right">{currencyFormatter.format(op.baseSalary)}</TableCell>
                    <TableCell align="right">{currencyFormatter.format(op.perLeadBonus)}</TableCell>
                    <TableCell align="right">
                      {op.volumeBonus > 0 ? currencyFormatter.format(op.volumeBonus) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {currencyFormatter.format(op.totalSalary)}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>ИТОГО</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {numberFormatter.format(report.totalHours)}
                  </TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {numberFormatter.format(report.totalCalls)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {report.totalLeads}
                  </TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {currencyFormatter.format(report.totalSalary)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {report.operators.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Нет данных по операторам за выбранный период
              </Typography>
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default OperatorReportPage;
